/* Wizard · Step 6 · Review & Submit
 * Extracted iter101.91 from `/app/frontend/src/pages/doctor/CaseSubmissionPage.jsx`.
 */
import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { confirmDialog, alertDialog, promptDialog } from "@/lib/confirm";
import {
    CaretRight, CaretLeft, CaretDown, CheckCircle, Circle, Upload, X, Check,
    MagnifyingGlass, ArrowsCounterClockwise, Crown, FileArrowUp, GitBranch,
    Warning, Lightning, Clock, Info, Tooth, Star, ShieldCheck, Cube, Cloud,
    CloudArrowUp, File as FileIcon, FilePdf, FileZip, FileVideo, Sparkle, Tag,
    ClipboardText, HeartStraight, Ruler, TrendUp, Sliders, Package, Prohibit,
    Notepad, ListBullets, Pencil, ArrowSquareUp, ArrowSquareDown, CircleHalf,
    Printer, BookmarkSimple, FloppyDisk, Trash, ChartBar, Hourglass, Calculator,
    Lock, UserGear, Camera, Stethoscope, HouseLine, Plus, Prescription,
    Headset, Truck, Receipt, SealCheck, Buildings, Hash, Scan, ArrowRight,
    User as UserIcon,
} from "@phosphor-icons/react";
import PerToothPlanAccordion from "@/components/case/PerToothPlanAccordion";
import DetailedClinicalExam, { ExamAccordion, present } from "@/components/case/DetailedClinicalExam";
import TreatmentObjectives from "@/components/case/TreatmentObjectives";
import CaseSubmittedDialog from "@/components/case/CaseSubmittedDialog";
import PlanTimelineSection, { RetentionRecallPair } from "@/components/case/PlanTimelineSection";
import AdvancePaymentBlock from "@/components/case/AdvancePaymentBlock";
import RequestScanVisitModal from "@/components/patient/RequestScanVisitModal";
import PatientAvatar from "@/components/patient/PatientAvatar";
import AddPatientDialog from "@/components/doctor/AddPatientDialog";
import DemoFillButton from "@/components/case-wizard/DemoFillButton";
import PrescriptionComplianceBlock from "@/components/case/PrescriptionComplianceBlock";
import WizardPaymentCommitment from "@/components/case/WizardPaymentCommitment";
import InvisiqueLogo from "@/components/site/Logo";
import {
    LabSection, LabChip, LabChoiceCard, LabInfoNote, LabGlanceRow,
    FdiChipPicker, PhaseSplitVisualizer,
} from "@/components/case/wizard/lab-section";
import {
    SelfPlanLabInstructions, StepLabInstructions, TierInclusionRow,
} from "@/components/case/wizard/steps/StepLabInstructions";
import {
    DraftSavedChip, ChildCaseBanner, StepIndicator, WizardStepHero,
    Step1SectionHeader, Step1ProgressSpine, Step1AnsweredStack,
    Step1AnsweredStrip, Step1ActiveWrap, Step1RecapBanner, RecapChip,
    ReviewBlock, Row, SelectField, SelectedPill,
    cardBadgeClass, resolveHeroIcon, fmtINR,
} from "@/components/case/wizard/shared";
import {
    ScanSourcePicker, RadioRailRow, CoeSupportCompliancePicker,
    PlanningModePicker, ModeCard,
} from "@/components/case/wizard/pickers";
import {
    IOScannerToggleCard, TierPicker, TierFeature, LabVisitScanFeeRow,
    PromoBlock, PricingAndOffersCard, PrescriptionPreviewCard,
    PreviewBlock, PreviewRow, CoeOrderPreviewCard, PriorityPicker,
    SlotUploader, PlanningSoftwareExportCard, CoeHandoffCallout,
} from "@/components/case/wizard/blocks";


export function StepReview({ patient, diagnosis, notes, clinical, rx, uploads, draftCase, pricing, setPricing, setRx, planningMode, draftCaseId, ensureDraft, autoTier = null, planTimeline = null, priorityTiers = [], ioScannerFee = 3000, promotions = [], doctorProfile = null, prescriptionSigned = false, setPrescriptionSigned = () => {}, initialCompliance = null, onDoctorProfileUpdate = () => {}, signApiRef = null, scanSource = "", coeSupportCompliance = null, complianceFee = 0 }) {
    const uploadedSlots = Object.keys(uploads).filter((k) => uploads[k]?.uploaded);
    // iter86 Wave C-lite — surface the authoring mode so the doctor sees
    // clearly what they signed up for at the top of the review.
    const isCoeMode = planningMode === "coe";
    // iter84 Wave A — surface the new clinical/anamnesis/lab fields when
    // populated. Empty values stay hidden so the review stays terse.
    const hasAnamnesis = clinical.medical_history || clinical.missing_teeth || clinical.previous_ortho;
    const hasBite = clinical.overbite_mm !== "" && clinical.overbite_mm != null
        || clinical.overjet_mm !== "" && clinical.overjet_mm != null;
    const hasScanMeta = clinical.scan_device || clinical.scan_date || clinical.scan_quality_confirmed;
    // iter87 Wave D — detailed clinical exam summary; surfaced only if
    // at least one of the structured fields is populated.
    const hasDetailedExam = !!(
        clinical.angle_class_right || clinical.angle_class_left || clinical.skeletal_class
        || clinical.vertical_dimension || clinical.midline_relationship
        || (clinical.upper_arch_crowding_mm !== "" && clinical.upper_arch_crowding_mm != null)
        || (clinical.lower_arch_crowding_mm !== "" && clinical.lower_arch_crowding_mm != null)
        || clinical.buccal_occlusion_right || clinical.buccal_occlusion_left
        || clinical.anterior_guidance || clinical.posterior_crossbite
        || clinical.anterior_crossbite || clinical.functional_shift
        || clinical.periodontal_health || clinical.plaque_index
        || clinical.scan_model || clinical.cbct_available
    );
    const hasLab = !!rx.lab_notes;
    // iter85 Wave B — surface per-tooth auxiliaries when planned.
    const hasAttachments = (rx.attachments_plan || []).length > 0;
    const hasCuts = (rx.precision_cuts || []).length > 0;
    const hasRamps = rx.bite_ramps?.enabled;
    return (
        <div className="space-y-3 text-sm">
            {/* iter98.99ar — Preview launchers reorganised per user direction:
                the two big single-column banners (Preview the case + Preview
                prescription) are now compact side-by-side cards. Both retain
                their own Dialog + content — only the trigger surfaces shrank
                to a uniform slim card matching the existing PrescriptionPreviewCard
                style. On <sm the cards stack so doctors on mobile keep a one-
                column flow. Applies to both Self-Plan and CoE flows. */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="case-wizard-step6-preview-row">

            {/* ── Preview the Case (compact) ── */}
            <Dialog>
                <DialogTrigger asChild>
                    <button
                        type="button"
                        className="group w-full text-left rounded-xl border border-border bg-card hover:border-primary/30 hover:bg-primary/[0.03] transition-colors px-4 py-3 flex items-center gap-3"
                        data-testid="case-wizard-preview-launcher"
                    >
                        <div className="shrink-0 w-9 h-9 rounded-lg bg-primary/10 text-primary grid place-items-center ring-1 ring-primary/20">
                            <MagnifyingGlass size={16} weight="duotone" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="font-display text-[13.5px] font-semibold text-foreground leading-tight">
                                Preview the case
                            </div>
                            {/* iter98.99ar — Compact fill counter (sections
                                captured / records uploaded). Kept from the
                                original hero so doctors still get an at-a-
                                glance read on draft completeness without
                                opening the dialog. */}
                            {(() => {
                                const checks = [
                                    !!(patient?.name),
                                    !!diagnosis,
                                    !!(clinical?.malocclusion_class || clinical?.arch || clinical?.extraction_plan),
                                    hasAnamnesis,
                                    hasBite,
                                    hasScanMeta,
                                    hasDetailedExam,
                                    !!((rx?.objectives || []).length || rx?.ipr_preference || rx?.attachments_preference),
                                    hasLab,
                                    uploadedSlots.length > 0,
                                ];
                                const filledCount = checks.filter(Boolean).length;
                                const totalCount  = checks.length;
                                return (
                                    <div
                                        className="text-[11.5px] text-muted-foreground leading-snug mt-0.5"
                                        data-testid="case-wizard-preview-fill-stats"
                                    >
                                        <span className="font-semibold text-foreground tabular-nums">{filledCount}</span>
                                        <span> of {totalCount} sections captured</span>
                                        {uploadedSlots.length > 0 && (
                                            <>
                                                <span className="mx-1 text-muted-foreground/40">·</span>
                                                <span className="font-semibold text-foreground tabular-nums">{uploadedSlots.length}</span>
                                                <span> record{uploadedSlots.length === 1 ? "" : "s"}</span>
                                            </>
                                        )}
                                    </div>
                                );
                            })()}
                        </div>
                        <span
                            className="shrink-0 inline-flex items-center gap-1 text-[12px] font-semibold text-primary"
                            data-testid="case-wizard-preview-open-btn"
                        >
                            Preview
                            <CaretRight size={12} weight="bold" className="group-hover:translate-x-0.5 transition-transform" />
                        </span>
                    </button>
                </DialogTrigger>
                <DialogContent
                    className="max-w-3xl max-h-[90vh] overflow-y-auto p-0 bg-secondary/20"
                    data-testid="case-wizard-preview-dialog"
                >
                    <DialogTitle className="sr-only">Case preview</DialogTitle>
                    <DialogDescription className="sr-only">
                        Read-only snapshot of the case as it will be submitted. Close the dialog to make any edits.
                    </DialogDescription>

                    {/* ─── Document — paper card mimicking the
                        downloaded Treatment-Plan PDF aesthetic ─── */}
                    <article className="m-4 bg-card rounded-2xl shadow-[0_8px_32px_-8px_rgba(15,23,42,0.12)] ring-1 ring-border/70 overflow-hidden">

                        {/* Letterhead — gradient bg + radial stamp pattern
                            + brand logo (left) and document meta (right). */}
                        <header
                            className="relative px-6 sm:px-8 pt-6 pb-5 bg-gradient-to-br from-primary/[0.06] via-card to-card border-b border-border/70"
                            data-testid="case-preview-letterhead"
                        >
                            <div
                                aria-hidden
                                className="absolute inset-0 pointer-events-none opacity-[0.04]"
                                style={{ backgroundImage: "radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)", backgroundSize: "24px 24px" }}
                            />
                            <div className="relative flex items-start justify-between gap-4 flex-wrap">
                                <InvisiqueLogo size="lg" showTagline />
                                <div className="text-right">
                                    <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-primary">
                                        Case Submission
                                    </div>
                                    <div className="font-display text-xl font-bold text-foreground tracking-tight leading-tight mt-1">
                                        Pre-submission Preview
                                    </div>
                                    <div className="text-[11.5px] text-muted-foreground mt-1 tabular-nums">
                                        Draft · {draftCase?.code || "(code on save)"} · {new Date().toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                                    </div>
                                </div>
                            </div>
                            <div className="relative mt-4 flex items-center justify-between gap-3 flex-wrap">
                                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-card/80 ring-1 ring-border text-[11px] font-semibold text-foreground/80">
                                    <SealCheck size={13} weight="fill" className="text-primary" />
                                    Read-only snapshot — close to make any edits
                                </div>
                                <span
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-[11px] font-bold uppercase tracking-[0.14em] bg-primary/10 text-primary border-primary/30"
                                >
                                    <Sparkle size={11} weight="fill" /> Draft
                                </span>
                            </div>
                        </header>

                        {/* Identity block — Patient + Doctor cards */}
                        <section className="px-6 sm:px-8 py-5 border-b border-border/70 grid grid-cols-1 md:grid-cols-2 gap-5">
                            <div>
                                <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-muted-foreground mb-2">
                                    Patient
                                </div>
                                <div className="flex items-start gap-3">
                                    {/* iter101.58 — PatientAvatar `xl` for
                                        parity with the Rx preview + doctor
                                        portal identity headers. */}
                                    <PatientAvatar patient={patient} size="xl" className="ring-2 ring-primary/20" />
                                    <div className="min-w-0">
                                        <div className="font-display text-base font-bold text-foreground leading-tight">
                                            {patient?.name || "—"}
                                        </div>
                                        <div className="text-[12px] text-muted-foreground flex items-center gap-x-3 gap-y-1 flex-wrap mt-1">
                                            {patient?.phone && <span className="tabular-nums">{patient.phone}</span>}
                                            {patient?.dob && <span><strong className="text-foreground/80">DOB</strong> · {patient.dob}</span>}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div>
                                <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-muted-foreground mb-2">
                                    Treating Doctor
                                </div>
                                <div className="flex items-start gap-3">
                                    <div className="shrink-0 w-16 h-16 rounded-full bg-secondary text-foreground/80 grid place-items-center ring-1 ring-border">
                                        <Stethoscope size={24} weight="duotone" />
                                    </div>
                                    <div className="min-w-0">
                                        <div className="font-display text-base font-bold text-foreground leading-tight">
                                            {doctorProfile?.name || "—"}
                                        </div>
                                        {draftCase?.code && (
                                            <div className="text-[10.5px] text-muted-foreground/80 mt-1 tabular-nums inline-flex items-center gap-1">
                                                <Hash size={10} weight="bold" /> {draftCase.code}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* Document body — sections */}
                        <div className="px-6 sm:px-8 py-6 space-y-5 bg-card">
                        <ReviewBlock title="Diagnosis">
                            <Row k="Diagnosis" v={diagnosis || "—"} />
                            {notes && <Row k="Notes" v={notes} />}
                        </ReviewBlock>
                        <ReviewBlock title="Clinical">
                            <Row k="Malocclusion" v={clinical.malocclusion_class ? `Class ${clinical.malocclusion_class}` : "—"} />
                            <Row k="Arch" v={clinical.arch || "—"} />
                            <Row k="Extractions" v={(clinical.extraction_plan || "—") + (clinical.extraction_teeth ? ` · teeth ${clinical.extraction_teeth}` : "")} />
                            {clinical.perio_status && <Row k="Perio" v={clinical.perio_status} />}
                            {clinical.chief_complaint && <Row k="Chief complaint" v={clinical.chief_complaint} />}
                            {clinical.special_notes && <Row k="Special notes" v={clinical.special_notes} />}
                        </ReviewBlock>
                        {hasAnamnesis && (
                            <ReviewBlock title="Medical & ortho history">
                                {clinical.medical_history && <Row k="Medical" v={clinical.medical_history} />}
                                {clinical.missing_teeth && <Row k="Missing teeth" v={clinical.missing_teeth} />}
                                {clinical.previous_ortho && (
                                    <Row
                                        k="Previous ortho"
                                        v={clinical.previous_ortho + (clinical.previous_ortho_notes ? ` · ${clinical.previous_ortho_notes}` : "")}
                                    />
                                )}
                            </ReviewBlock>
                        )}
                        {hasBite && (
                            <ReviewBlock title="Bite measurements">
                                {clinical.overbite_mm !== "" && clinical.overbite_mm != null && (
                                    <Row k="Overbite" v={`${clinical.overbite_mm} mm`} />
                                )}
                                {clinical.overjet_mm !== "" && clinical.overjet_mm != null && (
                                    <Row k="Overjet" v={`${clinical.overjet_mm} mm`} />
                                )}
                            </ReviewBlock>
                        )}
                        {hasScanMeta && (
                            <ReviewBlock title="Scan provenance">
                                {clinical.scan_device && <Row k="Device" v={clinical.scan_device.replace(/_/g, " ")} />}
                                {clinical.scan_model && <Row k="Model" v={clinical.scan_model} />}
                                {clinical.scan_date && <Row k="Scan date" v={clinical.scan_date} />}
                                <Row
                                    k="QC confirmed"
                                    v={clinical.scan_quality_confirmed ? "Yes — full-arch, artifact-free" : "Not confirmed"}
                                />
                                {clinical.cbct_available && <Row k="CBCT" v="Available" />}
                            </ReviewBlock>
                        )}
                        {hasDetailedExam && (
                            <ReviewBlock title="Detailed clinical exam">
                                {(clinical.angle_class_right || clinical.angle_class_left || clinical.angle_class_anterior) && (
                                    <Row
                                        k="Angle (R/L/Ant)"
                                        v={[
                                            clinical.angle_class_right,
                                            clinical.angle_class_left,
                                            clinical.angle_class_anterior,
                                        ].map((a) => (a ? a.replace("class_", "Cl ").replace("_", " ") : "—")).join(" / ")}
                                    />
                                )}
                                {clinical.skeletal_class && (
                                    <Row k="Skeletal" v={clinical.skeletal_class.replace("class_", "Class ").replace("_", " ")} />
                                )}
                                {clinical.vertical_dimension && <Row k="Vertical" v={clinical.vertical_dimension} />}
                                {clinical.midline_relationship && (
                                    <Row
                                        k="Midline"
                                        v={
                                            clinical.midline_relationship.replace(/_/g, " ") +
                                            (clinical.midline_shift_mm ? ` · ${clinical.midline_shift_mm} mm` : "")
                                        }
                                    />
                                )}
                                {(clinical.upper_arch_crowding_mm !== "" && clinical.upper_arch_crowding_mm != null) && (
                                    <Row k="Upper crowding" v={`${clinical.upper_arch_crowding_mm} mm`} />
                                )}
                                {(clinical.lower_arch_crowding_mm !== "" && clinical.lower_arch_crowding_mm != null) && (
                                    <Row k="Lower crowding" v={`${clinical.lower_arch_crowding_mm} mm`} />
                                )}
                                {clinical.crowding_distribution && (
                                    <Row k="Distribution" v={clinical.crowding_distribution} />
                                )}
                                {(clinical.buccal_occlusion_right || clinical.buccal_occlusion_left) && (
                                    <Row
                                        k="Buccal occlusion R/L"
                                        v={[clinical.buccal_occlusion_right, clinical.buccal_occlusion_left]
                                            .map((b) => (b ? b.replace("class_", "Cl ").replace(/_/g, " ") : "—"))
                                            .join(" / ")}
                                    />
                                )}
                                {(clinical.canine_class_right || clinical.canine_class_left) && (
                                    <Row
                                        k="Canine class R/L"
                                        v={[clinical.canine_class_right, clinical.canine_class_left]
                                            .map((c) => (c ? c.replace("class_", "Cl ").replace(/_/g, " ") : "—"))
                                            .join(" / ")}
                                    />
                                )}
                                {clinical.posterior_crossbite && (
                                    <Row k="Posterior crossbite" v={clinical.posterior_crossbite_location || "yes"} />
                                )}
                                {clinical.anterior_crossbite && <Row k="Anterior crossbite" v="yes" />}
                                {clinical.anterior_guidance && (
                                    <Row k="Anterior guidance" v={clinical.anterior_guidance} />
                                )}
                                {(clinical.lateral_movement_right || clinical.lateral_movement_left || clinical.protrusive_movement) && (
                                    <Row
                                        k="Functional"
                                        v={`R ${clinical.lateral_movement_right || "—"} / L ${clinical.lateral_movement_left || "—"} / Pro ${clinical.protrusive_movement || "—"}`}
                                    />
                                )}
                                {clinical.functional_shift && <Row k="Functional shift" v="noted" />}
                                {clinical.periodontal_health && (
                                    <Row
                                        k="Perio"
                                        v={
                                            clinical.periodontal_health +
                                            (clinical.plaque_index ? ` · plaque ${clinical.plaque_index}` : "") +
                                            (clinical.perio_compliance ? ` · compliance ${clinical.perio_compliance}` : "") +
                                            (clinical.gingival_recession ? " · recession" : "")
                                        }
                                    />
                                )}
                            </ReviewBlock>
                        )}
                        <ReviewBlock title="Prescription">
                            <Row k="Objectives" v={(rx.objectives || []).length ? rx.objectives.join(", ") : "—"} />
                            {rx.ipr_preference && <Row k="IPR" v={rx.ipr_preference} />}
                            {rx.attachments_preference && <Row k="Attachments" v={rx.attachments_preference} />}
                            {rx.elastics_preference && <Row k="Elastics" v={rx.elastics_preference} />}
                            {rx.do_not_touch_teeth && <Row k="Do-not-touch" v={rx.do_not_touch_teeth} />}
                            {rx.esthetic_goals && <Row k="Esthetic goals" v={rx.esthetic_goals} />}
                        </ReviewBlock>
                        {hasLab && (
                            <ReviewBlock title="Lab instructions">
                                {rx.lab_notes && <Row k="Notes for lab" v={rx.lab_notes} />}
                            </ReviewBlock>
                        )}
                        {(hasAttachments || hasCuts || hasRamps || (rx.tooth_movements_plan || []).length > 0) && (
                            <ReviewBlock title="Per-tooth auxiliaries">
                                {(rx.tooth_movements_plan || []).length > 0 && (
                                    <Row
                                        k="Tooth movements"
                                        v={`${rx.tooth_movements_plan.length} ${rx.tooth_movements_plan.length === 1 ? "tooth" : "teeth"} · ${rx.tooth_movements_plan.map((m) => m.tooth).join(", ")}`}
                                    />
                                )}
                                {hasAttachments && (
                                    <Row
                                        k="Attachments"
                                        v={`${rx.attachments_plan.length} ${rx.attachments_plan.length === 1 ? "tooth" : "teeth"} · ${rx.attachments_plan.map((a) => a.tooth).join(", ")}`}
                                    />
                                )}
                                {hasCuts && (
                                    <Row
                                        k="Precision cuts"
                                        v={`${rx.precision_cuts.length} · ${rx.precision_cuts.map((c) => `${c.tooth}${c.position && c.position !== "buccal" ? `(${c.position})` : ""}`).join(", ")}`}
                                    />
                                )}
                                {hasRamps && (
                                    <Row
                                        k="Bite ramps"
                                        v={
                                            (rx.bite_ramps.position || "configured").replace(/_/g, " ") +
                                            ((rx.bite_ramps.teeth || []).length > 0
                                                ? ` · ${rx.bite_ramps.teeth.join(", ")}`
                                                : "")
                                        }
                                    />
                                )}
                            </ReviewBlock>
                        )}
                        <ReviewBlock title={`Records (${uploadedSlots.length})`}>
                            {uploadedSlots.length === 0 && (
                                <div className="text-xs text-muted-foreground italic">No records uploaded — you can add them after submitting.</div>
                            )}
                            <div className="flex flex-wrap gap-1.5">
                                {uploadedSlots.map((k) => (
                                    <span key={k} className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 inline-flex items-center gap-1">
                                        <Check size={10} weight="bold" /> {k}
                                    </span>
                                ))}
                            </div>
                        </ReviewBlock>
                        </div>

                        {/* Document footer — mirrors the PDF footer line */}
                        <footer className="px-6 sm:px-8 py-3 border-t border-border/70 bg-secondary/30 text-center">
                            <div className="text-[10px] text-muted-foreground/80 tracking-wider">
                                Draft preview — not yet sent to the lab. Submit the case to dispatch this order.
                            </div>
                        </footer>
                    </article>
                </DialogContent>
            </Dialog>

            {/* iter98.99ar — Preview prescription companion card.
                Same compact card style as "Preview the case" on the
                left; together they occupy a two-column grid on Step 6
                so both previews are equally weighted (no longer a
                hero + sub-card hierarchy). Still launches the same
                Rx-fidelity dialog underneath. */}
            <PrescriptionPreviewCard
                patient={patient}
                doctorProfile={doctorProfile}
                draftCase={draftCase}
                diagnosis={diagnosis}
                notes={notes}
                clinical={clinical}
                rx={rx}
                planTimeline={planTimeline}
                planningMode={planningMode}
            />

            </div>{/* /grid */}

            {/* iter98.99w — Authoring-mode badge removed at user direction.
                Step hero already labels this step as "Review & Submit",
                and the auto-tier card below carries the planning mode
                through pricing — a separate "Submitting as a
                self-planned case" pill here was redundant. */}

            {/* iter98.99o — Auto-tier hero card for Self-Plan.
                Shows the tier the system assigned based on the
                Upper + Lower aligner counts the doctor entered in
                Lab Instructions. If the total exceeds the max tier
                cap (typically 40), shows a blocking error banner
                instead so the doctor can revise before submit.
                Hidden for CoE (planners set tier on their side). */}
            {!isCoeMode && autoTier?.isReady && (
                autoTier.error ? (
                    <div
                        className="rounded-2xl border-2 border-rose-300 bg-rose-50 px-4 py-3.5 flex items-start gap-3"
                        data-testid="case-wizard-tier-overflow-error"
                    >
                        <div className="shrink-0 w-9 h-9 rounded-xl bg-rose-100 text-rose-700 grid place-items-center ring-1 ring-rose-200">
                            <Warning size={18} weight="fill" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <h3 className="font-display text-[14px] font-semibold text-rose-800 leading-tight">
                                Plan exceeds maximum aligners
                            </h3>
                            <p className="text-[12px] text-rose-700/90 mt-1 leading-snug">
                                {autoTier.error}
                            </p>
                            <p className="text-[11px] text-rose-600/80 mt-1.5 leading-snug">
                                Go back to Step 5 (Lab Instructions) and reduce the upper or lower aligner count.
                            </p>
                        </div>
                    </div>
                ) : autoTier.tier && (
                    /* iter98.99az — Slimmed tier & pricing card.
                       Previous design (iter98.99aa) had 4 visual layers:
                       eyebrow + tier-name row + 3-col grid + helper line —
                       too much chrome for what is essentially 5 facts.
                       New layout: a single compact card with the eyebrow
                       chip in the top-left, tier name + range inline, and
                       a one-line meta strip with case-specific info +
                       refinement/retainer allowance. ~80px shorter and
                       reads in a single eye-sweep. */
                    <div
                        className="rounded-2xl border border-border bg-card px-5 py-4"
                        data-testid="case-wizard-tier-auto-card"
                    >
                        <div className="flex items-baseline justify-between gap-3 flex-wrap">
                            <div className="min-w-0 flex-1">
                                <span
                                    className="inline-flex items-center gap-1 text-[9.5px] font-bold tracking-[0.16em] uppercase text-emerald-700"
                                    data-testid="case-wizard-tier-auto-chip"
                                >
                                    <Check size={10} weight="bold" /> Auto-assigned tier
                                </span>
                                <div className="mt-1 font-display text-[20px] sm:text-[22px] font-semibold text-foreground leading-tight" data-testid="case-wizard-tier-name">
                                    {autoTier.tier.name}
                                    {(() => {
                                        const t = autoTier.tier;
                                        const mn = Number(t.aligner_min) || 0;
                                        const mx = Number(t.aligner_count) || 0;
                                        if (!mx) return null;
                                        const rangeCopy = mn > 0 ? `${mn}-${mx} aligners` : `up to ${mx} aligners`;
                                        return (
                                            <span
                                                className="text-[12.5px] font-normal text-muted-foreground ml-2"
                                                data-testid="case-wizard-tier-meta"
                                            >
                                                {rangeCopy}
                                            </span>
                                        );
                                    })()}
                                </div>
                            </div>
                            <div className="flex items-baseline gap-1.5 shrink-0">
                                <span className="font-display text-[22px] sm:text-[26px] font-bold text-foreground leading-none tabular-nums" data-testid="case-wizard-tier-price">
                                    {(() => {
                                        const raw = Number(autoTier.tier.lab_charge || 0);
                                        const d = Number(autoTier.tier.self_plan_discount || 0);
                                        const dt = (autoTier.tier.self_plan_discount_type || "flat").toLowerCase();
                                        const eff = dt === "percent"
                                            ? Math.max(0, raw * (1 - d / 100))
                                            : Math.max(0, raw - d);
                                        return `₹${eff.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
                                    })()}
                                </span>
                                <span className="text-[11px] text-muted-foreground">lab charge</span>
                            </div>
                        </div>

                        {/* One-line meta strip — case-specific + deliverables.
                            Joins individual facts with bullets; line wraps
                            naturally on narrow viewports. */}
                        <div className="mt-3 pt-2.5 border-t border-border/60 text-[11.5px] text-muted-foreground leading-snug" data-testid="case-wizard-tier-meta-strip">
                            <span className="text-foreground/80 font-medium tabular-nums">{autoTier.total} aligners</span>
                            <span className="ml-1 tabular-nums">({Number(planTimeline?.aligner_count_upper) || 0}U / {Number(planTimeline?.aligner_count_lower) || 0}L)</span>
                            {autoTier.tier.refinement_included && (
                                <>
                                    <span className="mx-1.5 text-muted-foreground/40">·</span>
                                    <span>{autoTier.tier.refinement_count || 1}× refinement{autoTier.tier.refinement_aligner_count ? ` (up to ${autoTier.tier.refinement_aligner_count} aligners)` : ""}</span>
                                </>
                            )}
                            {autoTier.tier.retainers_included && (
                                <>
                                    <span className="mx-1.5 text-muted-foreground/40">·</span>
                                    <span>{autoTier.tier.included_retainer_count || 1} retainer set{(autoTier.tier.included_retainer_count || 1) === 1 ? "" : "s"}</span>
                                </>
                            )}
                        </div>
                    </div>
                )
            )}



            {/* ── Self-Plan only: Payment & Offers card ──
                Renders the existing PricingAndOffersCard (tier line +
                Manufacturing speed + IO Scanner add-on + promo code +
                final total). Hidden for CoE because the planner sets
                the tier on their side later. */}
            {!isCoeMode && (
                <PricingAndOffersCard
                    pricing={pricing}
                    setPricing={setPricing}
                    promotions={promotions}
                />
            )}

            {/* iter98.99de — Payment commitment widget. Self-Plan only.
                Mirrors the Pay-later / Pay-advance / Pay-in-full triad
                from the CoE flow's ApprovePayDialog so doctors get the
                same payment-commitment chrome whether they self-planned
                or had Invisique plan the case. Selection is written to
                `pricing.payment_mode` + `pricing.advance_amount` and
                consumed by submitCase() — on Submit, if mode != "later"
                we POST /cases/{id}/advance-intent so the pay-link is
                minted in the same click. */}
            {!isCoeMode && (
                <WizardPaymentCommitment
                    pricing={pricing}
                    setPricing={setPricing}
                    finalCharge={Number(pricing?.final_charge || 0)}
                />
            )}

            {/* ── CoE only: Order preview ── iter98.99al
                Read-only summary of the doctor's submission-time
                choices (Manufacturing Speed + IO Scanner). The tier
                line shows a "pending planner" placeholder — the
                planner picks it after they finish authoring the plan
                and the full Payment & Offers card (incl. promo input)
                surfaces on the Plan Approval screen once the case is
                sent back for the doctor's approval. */}
            {isCoeMode && (
                <CoeOrderPreviewCard
                    pricing={pricing}
                    ioScannerFee={ioScannerFee}
                    scanSource={scanSource}
                    planningMode={planningMode}
                    coeSupportCompliance={coeSupportCompliance}
                    complianceFee={complianceFee}
                />
            )}

            {/* iter98.99w — Scan provenance / Detailed clinical exam /
                Lab notes / Per-tooth / Records ReviewBlocks removed at
                user direction from the inline body. They duplicated the
                upstream-step data; doctors verify via the "Preview the
                case" Dialog above.
                Feasibility checks block also removed — the soft-warning
                acknowledgment ladder lives in computeFeasibility for
                CoE planners on their side; Self-Plan doctors carry the
                clinical responsibility and we keep Step 6 lean. */}

            {/* iter98.99x — Pre-submit verification checklist removed from
                Step 6 per user direction. The verification gate at the
                submit button is now driven by feasibility + tier overflow
                only; the explicit 5-box checklist was perceived as a
                speed-bump on a step already meant to be a confirm-and-
                submit screen. */}
            {/* iter97.74 — Advance payment removed from Review entirely.
                Each flow now has its own dedicated "money screen":
                CoE → Step 1 (where they pick the tier), Self-Plan →
                Step 6 (Choose Tier). Review stays focused on
                case-content verification. */}

            {/* iter98.99an — Prescription compliance + e-signature gate
                (Indian medical regulations · IT Act / NMC Guidelines).
                Captures the doctor's signature + 4 mandatory consent
                boxes BEFORE the wizard's Submit button unlocks. The
                generated prescription PDF embeds the signature and is
                retained on the case record for 7 years per medical-
                records retention norms. Rendered for both Self-Plan
                AND CoE doctors. */}
            <PrescriptionComplianceBlock
                caseId={draftCaseId}
                caseStatus={draftCase?.status}
                doctorProfile={doctorProfile}
                initialCompliance={initialCompliance}
                onSignedChange={setPrescriptionSigned}
                onProfileUpdate={onDoctorProfileUpdate}
                signApiRef={signApiRef}
            />
        </div>
    );
}
